import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { AppRoutesPaths, ROUTE_OWNER } from './types';
import { Loading } from '@sandtech-hub-monorepo/ui-kit-site';
import {
  useCognito,
  useGetMaybeViewer,
} from '@sandtech-hub-monorepo/ui-auth-kit';

import {
  LearnerPathProvider,
  useLearnerPathQuery,
} from '../data/useLearnerPath';

export const GuardedRoutes: React.FC = () => {
  const { currentUser, loading } = useCognito();
  const { viewer, loading: viewerLoading } = useGetMaybeViewer();
  const location = useLocation();

  // Skipped until there is somebody to ask about; an unauthenticated call
  // would be refused, and the redirect to Login below happens first anyway.
  const {
    path,
    isActiveUniversityStudent,
    loading: pathLoading,
  } = useLearnerPathQuery({
    skip: !currentUser,
  });

  if (loading) {
    return <Loading />;
  }

  if (!currentUser && !loading) {
    return <Navigate to={AppRoutesPaths.Login} replace={true} />;
  }

  // Wait for the viewer before deciding whether the profile is complete,
  // otherwise a signed-in user briefly falls through the gate on first paint.
  if (viewerLoading) {
    return <Loading />;
  }

  /*
   * Onboarding is for new accounts only. Anyone already holding a program
   * enrollment predates it — they were admitted through the degree side, where
   * gender and date of birth were never asked for — so gating them here would
   * stop an existing learner reaching the app they have been using, to collect
   * fields nothing on their path needs.
   *
   * Any enrollment counts, including FAILED and GRADUATED: the question is
   * whether this account existed before, not whether it is currently active.
   * `programEnrollments { id }` is already on the `Viewer` query, so this
   * costs no extra request.
   */
  const isExistingLearner = (viewer?.programEnrollments ?? []).length > 0;

  // Hard onboarding gate: a signed-in learner who hasn't supplied the required
  // identity fields (gender + date of birth) is redirected to Onboarding and
  // blocked from the rest of the app. The Onboarding route itself is exempt to
  // avoid a redirect loop.
  const profileIncomplete =
    !!viewer && !isExistingLearner && (!viewer.gender || !viewer.birthDate);

  if (profileIncomplete && location.pathname !== AppRoutesPaths.Onboarding) {
    return <Navigate to={AppRoutesPaths.Onboarding} replace={true} />;
  }

  if (pathLoading) {
    return <Loading />;
  }

  /*
   * A learner can only open the routes their path owns. A degree learner has
   * no business in checkout, and a GSA learner has none in the undergraduate
   * home or a transcript — those surfaces assume an enrollment they do not
   * have. `both` owns everything, since they genuinely are both.
   *
   * Home is the one safe destination for either, because it renders per path
   * rather than belonging to one.
   */
  const owner = ROUTE_OWNER[location.pathname];

  if (owner && path !== 'both' && path !== owner) {
    return <Navigate to={AppRoutesPaths.Home} replace={true} />;
  }

  /*
   * Handed down rather than re-resolved: the path is settled by the time
   * anything below renders, so every consumer reads one value and nothing
   * repeats this query. It is also why those components have no loading state
   * of their own — `pathLoading` above is the only one there is.
   */
  return (
    <LearnerPathProvider value={{ path, isActiveUniversityStudent }}>
      <Outlet />
    </LearnerPathProvider>
  );
};
